/**
 * @file index.tsx
 *
 * @fileoverview Adds metatag information to the current page based on the url.
 */

import { useQuery } from '@apollo/client';
import Head from 'next/head';
import React, { useContext, useMemo } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import GET_ROUTE_INFO from '~/jobiqo/components/Metatag/graphql/metatags.gql';
import { MetatagsInclude } from '~/jobiqo/components/Metatag/MetatagInclude';
import { removeUnusedItemsFromQuerystring } from '~/jobiqo/components/Metatag/utils';
import { getCleanUrlPart } from '~/jobiqo/components/Search/utils/searchHelpers';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { AppContextInterface } from '~/jobiqo/models/app';
import { Metatag } from '~/jobiqo/types/types';
import { isCanonicalProvidedByChild } from '~/jobiqo/utils/routes';
import { getTranslatedLink, withLocalePrefix } from '~/jobiqo/utils/links';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

// Content that is not translated, so all languages share the default locale canonical.
const UNTRANSLATED_CONTENT_TYPES = ['Job', 'OrganizationProfile'];

interface MetatagsProps {
  /**
   * The current path.
   */
  path?: string;
  /**
   * The current host.
   */
  host?: string;
}

export const Metatags = ({ path = '', host = '' }: MetatagsProps) => {
  const { APP } = useFrontendConfig();
  const appContext: AppContextInterface = useContext(AppContext);
  const { pathname } = useContext(PathContext);

  const { error, data } = useQuery(GET_ROUTE_INFO, {
    variables: {
      slug: removeUnusedItemsFromQuerystring(path),
      language: appContext.locale,
    },
  });
  const cleanPath = useMemo(() => getCleanUrlPart(path), [path]);
  // Get translated link to make sure we use correct path translation as canonical.
  const translatedPath = useMemo(
    () => getTranslatedLink(cleanPath, appContext.locale),
    [cleanPath, appContext.locale],
  );

  if (
    error ||
    !data ||
    !data.route ||
    !data.route.content ||
    !data.route.content.metatags
  ) {
    const canonicalPath = withLocalePrefix(
      `${host}${translatedPath}`,
      host,
      APP.LOCALES,
      appContext.locale,
    );
    return (
      <LazyHydrate whenIdle={true}>
        <Head>
          {!isCanonicalProvidedByChild(path, pathname) && (
            <link rel="canonical" href={canonicalPath} key="canonical" />
          )}
        </Head>
      </LazyHydrate>
    );
  }

  const metaTags: Metatag[] = data?.route?.content?.metatags;
  const canonicalLocale = UNTRANSLATED_CONTENT_TYPES.includes(
    data.route.content.__typename,
  )
    ? APP.DEFAULT_LOCALE
    : appContext.locale;
  return (
    <MetatagsInclude
      metaTags={metaTags}
      host={host}
      path={path}
      canonicalLocale={canonicalLocale}
    />
  );
};
